<!-- Modal Header -->
<div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
  <div class="flex items-center space-x-3">
    <div class="flex items-center justify-center w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg">
      <mat-icon class="text-primary-600 dark:text-primary-400"
        svgIcon="heroicons_outline:document-duplicate"></mat-icon>
    </div>
    <div>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Flujo de Documentos
      </h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Visualización del flujo completo de documentos
      </p>
    </div>
  </div>
  <button mat-icon-button mat-dialog-close
    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
    <mat-icon svgIcon="heroicons_outline:x-mark"></mat-icon>
  </button>
</div>

<!-- Flow Container -->
<div class="flow-container bg-gray-50 dark:bg-gray-900 min-h-[600px] relative overflow-hidden">
  <f-flow fDraggable>
    <f-canvas fZoom (fCanvasChange)="onCanvasChanged($event)" class="w-full h-full">

      <!-- Document Nodes -->
      <div *ngFor="let doc of flowDocuments; let i = index" fNode fDragHandle fNodeInput fNodeOutput
        [fNodePosition]="doc.fNodePosition" [fInputId]="doc?.inputId" [fOutputId]="doc?.outputId"
        [fInputConnectableSide]="doc.fInputConnectableSide" [fOutputConnectableSide]="doc.fOutputConnectableSide"
        [ngClass]="{
             'current-document': isCurrentDocument(doc),
             'source-document': i === 0
           }" class="document-node">

        <!-- Node Card -->
        <mat-card class="node-card shadow-lg border-0 overflow-hidden min-w-[280px] max-w-[320px]">

          <!-- Header with Class Code Badge -->
          <!-- En la sección Document Nodes, actualiza el header -->
          <div class="node-header p-4 pb-2">
            <div class="flex items-center justify-between mb-2">
              <span class="class-badge px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide">
                {{ doc?.classCode }}
              </span>
              <div class="flex items-center space-x-2">
                <!-- Ver Detalle Button - Clickable -->
                <button *ngIf="isClickable(doc)" mat-icon-button
                  class="detail-button w-8 h-8 rounded-full"
                  (click)="goToOrder(doc)"
                  [matTooltip]="getTooltip(doc)">
                  <mat-icon class="text-sm" svgIcon="heroicons_outline:eye"></mat-icon>
                </button>

                <!-- Ver Detalle Button - Disabled -->
                <button *ngIf="!isClickable(doc)" mat-icon-button
                  disabled
                  class="detail-button w-8 h-8 rounded-full opacity-50"
                  [matTooltip]="getTooltip(doc)">
                  <mat-icon class="text-sm" svgIcon="heroicons_outline:eye-slash"></mat-icon>
                </button>

                <!-- Document Type Icon -->
                <div class="document-type-icon w-6 h-6 rounded flex items-center justify-center">
                  <mat-icon class="text-sm" svgIcon="heroicons_mini:document"></mat-icon>
                </div>

                <!-- Debug: Show connection IDs -->
                <div class="text-xs text-gray-400" *ngIf="showDebugInfo">
                  <span *ngIf="doc?.inputId" class="text-green-600">I</span>
                  <span *ngIf="doc?.outputId" class="text-blue-600">O</span>
                </div>
              </div>
            </div>

            <!-- Class Name -->
            <h3 class="node-title text-base font-semibold mb-1 leading-tight">
              {{ doc?.className }}
            </h3>
          </div>

          <!-- Content -->
          <mat-card-content class="px-4 pb-4">
            <!-- Document Type -->
            <div class="info-row mb-3">
              <div class="info-label text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Tipo de Documento
              </div>
              <div class="info-value text-sm text-gray-700 dark:text-gray-300 font-medium">
                {{ doc?.documentTypeName }}
              </div>
            </div>

            <!-- Serial Number -->
            <div class="info-row mb-3">
              <div class="info-label text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Número de Serie
              </div>
              <div
                class="info-value text-sm font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200">
                {{ doc.serial }}
              </div>
            </div>

            <!-- Connection IDs (Debug info) -->
            <div class="debug-info mt-3 pt-3 border-t border-gray-200 dark:border-gray-700"
              *ngIf="showDebugInfo && canUseDebugOptions">
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div *ngIf="doc?.inputId">
                  <span class="text-gray-400">Input:</span>
                  <span class="font-mono text-green-600">{{ doc?.inputId }}</span>
                </div>
                <div *ngIf="doc?.outputId">
                  <span class="text-gray-400">Output:</span>
                  <span class="font-mono text-blue-600">{{ doc?.outputId }}</span>
                </div>
              </div>
            </div>
          </mat-card-content>

          <!-- Current Document Indicator -->
          <div *ngIf="isCurrentDocument(doc)"
            class="current-indicator absolute top-2 left-2 w-3 h-3 bg-primary-500 rounded-full animate-pulse">
          </div>

          <!-- Source Document Indicator -->
          <div *ngIf="i === 0"
            class="source-indicator absolute top-2 left-2 w-3 h-3 bg-green-500 rounded-full animate-pulse">
          </div>

        </mat-card>
      </div>

      <!-- Connections - Moved after nodes for proper rendering order -->
      @for(connection of connections; track connection.outputId; let i = $index) {
      <f-connection   [fType]="type" *ngIf="!!flowDocuments?.length" fReassignDisabled="true"
        fOutputId="{{connection.outputId}}" fInputId="{{connection.inputId}}">
      </f-connection>
      }

    </f-canvas>
  </f-flow>

  <!-- Flow Controls -->
  <div class="flow-controls absolute top-4 right-4 flex flex-col space-y-2">
    <button mat-mini-fab color="primary" class="shadow-lg" (click)="fullScreen()" matTooltip="Pantalla completa">
      <mat-icon svgIcon="heroicons_outline:arrows-pointing-out"></mat-icon>
    </button>

    <button mat-mini-fab class="bg-green-500 text-white shadow-lg" (click)="centerView()" matTooltip="Centrar vista">
      <mat-icon svgIcon="heroicons_outline:viewfinder-circle"></mat-icon>
    </button>

    <button *ngIf="canUseDebugOptions" mat-mini-fab
      class="bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-lg" (click)="toggleDebugInfo()"
      matTooltip="Mostrar información de debug">
      <mat-icon svgIcon="heroicons_outline:information-circle"></mat-icon>
    </button>
  </div>

  <!-- Debug Panel -->
  <div *ngIf="showDebugInfo && canUseDebugOptions"
    class="debug-panel absolute top-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-700 max-w-xs">
    <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Debug Info</div>
    <div class="text-xs space-y-1">
      <div>Documentos: {{ flowDocuments?.length || 0 }}</div>
      <div>Conexiones: {{ connections?.length || 0 }}</div>
      <div *ngFor="let conn of connections; let i = index">
        Conn {{ i + 1 }}: {{ conn.outputId }} → {{ conn.inputId }}
      </div>
    </div>
  </div>

  <!-- Legend -->
  <div
    class="flow-legend absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-700">
    <div class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Leyenda</div>
    <div class="flex flex-col space-y-2 text-xs">
      <div class="flex items-center space-x-2">
        <div class="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
        <span class="text-gray-600 dark:text-gray-400">Documento actual</span>
      </div>
      <div class="flex items-center space-x-2">
        <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
        <span class="text-gray-600 dark:text-gray-400">Documento fuente</span>
      </div>
    </div>
  </div>
</div>